import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, X, HelpCircle, Star, Wrench, GripVertical, Play, FileText, Mail, MessageSquare, Image, Upload, Users, Newspaper, Share2, Trash2, Calendar, Check, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import { settingsApi, mediaApi } from '@/lib/api';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

// Module configurations with all details
const MODULE_CONFIG = {
  'nyza-studio': {
    title: 'NYZA-STUDIO Branding',
    icon: Image,
    description: 'Logo-Verwaltung für Light/Dark Mode'
  },
  featured: {
    title: 'Empfohlene Fahrzeuge',
    icon: Star,
    description: 'Einstellungen für die empfohlenen Fahrzeuge'
  },
  brands: {
    title: 'Marken & Partner',
    icon: Star,
    description: 'Verwalten Sie Marken-Logos für den Scroll-Bereich'
  },
  about: {
    title: 'Über uns-Bereich',
    icon: FileText,
    description: 'Texte und Bilder für den About-Bereich'
  },
  video: {
    title: 'Video-Bereich',
    icon: Play,
    description: 'Video-URL und Thumbnail konfigurieren'
  },
  services: {
    title: 'Werkstatt & Services',
    icon: Wrench,
    description: 'Verwalten Sie Ihre Service-Angebote'
  },
  newsletter: {
    title: 'Newsletter',
    icon: Mail,
    description: 'Newsletter-Texte anpassen'
  },
  contact: {
    title: 'Kontakt-Bereich',
    icon: MessageSquare,
    description: 'Kontaktinformationen und Formular-Texte'
  },
  faq: {
    title: 'FAQ-Bereich',
    icon: HelpCircle,
    description: 'Häufig gestellte Fragen verwalten'
  },
  team: {
    title: 'Team',
    icon: Users,
    description: 'Teammitglieder verwalten'
  },
  blog: {
    title: 'Blog & News',
    icon: Newspaper,
    description: 'Blog-Einstellungen'
  },
  social: {
    title: 'Social Media',
    icon: Share2,
    description: 'Social Media Links für den Footer'
  },
  'test-drive': {
    title: 'Probefahrt',
    icon: Calendar,
    description: 'Probefahrt-Buchung Einstellungen'
  },
  financing: {
    title: 'Finanzierungsrechner',
    icon: Calculator,
    description: 'Finanzierungsrechner-Einstellungen'
  }
};

export default function AdminModuleSettings() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editLang, setEditLang] = useState('de');
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const saveTimeoutRef = useRef(null);
  const [autoSaved, setAutoSaved] = useState(false);

  useEffect(() => {
    loadSettings();
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save with debounce
  const autoSave = useCallback(async (newSettings) => {
    setSaving(true);
    try {
      await settingsApi.update(newSettings);
      setAutoSaved(true);
      setTimeout(() => setAutoSaved(false), 2000);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }, []);

  const handleChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    // Clear previous timeout and set new one for auto-save
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      autoSave(newSettings);
    }, 1000);
  };

  const handleSave = async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    setSaving(true);
    try {
      await settingsApi.update(settings);
      toast.success('Gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // File upload handler for logos
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const response = await mediaApi.upload(file);
      const url = response.data.url;
      handleChange(uploadTarget, url);
      toast.success('Bild hochgeladen');
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openFileUpload = (target) => {
    setUploadTarget(target);
    fileInputRef.current?.click();
  };

  // Navigate back to settings with modules tab active
  const handleBack = () => {
    navigate('/admin/settings?tab=modules');
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  const config = MODULE_CONFIG[moduleId];
  if (!config) {
    return (
      <div className="p-4 md:p-8">
        <p className="text-red-500">Modul nicht gefunden: {moduleId}</p>
        <Button onClick={handleBack} className="mt-4">Zurück</Button>
      </div>
    );
  }

  const Icon = config.icon;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
      {/* Hidden file input for logo uploads */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/png,image/jpeg,image/svg+xml"
        className="hidden"
      />

      {/* Header - Mobile Optimized */}
      <div className="flex flex-col gap-4 mb-6 md:mb-8">
        {/* Top Row: Back + Title */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-[#F9F9F7] rounded-lg transition-colors shrink-0"
            data-testid="back-to-modules"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gold/10 rounded-lg flex items-center justify-center shrink-0">
              <Icon size={20} className="text-gold md:w-6 md:h-6" />
            </div>
            <div className="min-w-0">
              <h1 className="font-heading text-xl md:text-2xl truncate">{config.title}</h1>
              <p className="text-xs md:text-sm text-[#666666] truncate">{config.description}</p>
            </div>
          </div>
        </div>

        {/* Bottom Row: Language Toggle + Auto-Save Status */}
        <div className="flex items-center justify-between gap-3">
          {/* Language Toggle */}
          <div className="flex border border-[#E5E5E5] rounded-lg overflow-hidden">
            <button
              onClick={() => setEditLang('de')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${editLang === 'de' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
            >
              🇩🇪 DE
            </button>
            <button
              onClick={() => setEditLang('en')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${editLang === 'en' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
            >
              🇬🇧 EN
            </button>
          </div>
          
          {/* Auto-save status */}
          <div className="flex items-center gap-2">
            {saving && (
              <span className="text-sm text-[#666666] flex items-center gap-2">
                <div className="animate-spin w-4 h-4 border-2 border-gold border-t-transparent rounded-full" />
                Speichern...
              </span>
            )}
            {autoSaved && !saving && (
              <span className="text-sm text-green-600 flex items-center gap-2">
                <Check size={16} />
                Gespeichert
              </span>
            )}
          </div>
        </div>
      </div>

      {/* NYZA-STUDIO Logo Settings */}
      {moduleId === 'nyza-studio' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Logo für Light Mode</h3>
            <p className="text-sm text-[#666666] mb-4">
              Wird im Admin-Bereich und auf hellen Hintergründen angezeigt. Empfohlen: PNG mit transparentem Hintergrund.
            </p>
            <div className="flex items-center gap-4">
              {settings.logo_light_url ? (
                <div className="relative border border-[#E5E5E5] rounded-lg p-4 bg-[#F9F9F7]">
                  <img 
                    src={getImageUrl(settings.logo_light_url)} 
                    alt="Logo Light" 
                    className="h-16 object-contain"
                  />
                </div>
              ) : (
                <div className="border-2 border-dashed border-[#E5E5E5] rounded-lg p-8 bg-[#F9F9F7] flex items-center justify-center">
                  <span className="text-[#999999] text-sm">Kein Logo hochgeladen</span>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  onClick={() => openFileUpload('logo_light_url')}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Upload size={16} />
                  Hochladen
                </Button>
                {settings.logo_light_url && (
                  <Button
                    type="button"
                    onClick={() => handleChange('logo_light_url', null)}
                    className="text-red-500 hover:text-red-700 flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    Entfernen
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Logo für Dark Mode</h3>
            <p className="text-sm text-[#666666] mb-4">
              Wird auf dunklen Hintergründen angezeigt (falls Dark Mode aktiviert ist). Empfohlen: Helle oder weiße Version des Logos.
            </p>
            <div className="flex items-center gap-4">
              {settings.logo_dark_url ? (
                <div className="relative border border-[#333] rounded-lg p-4 bg-[#1a1a1a]">
                  <img 
                    src={getImageUrl(settings.logo_dark_url)} 
                    alt="Logo Dark" 
                    className="h-16 object-contain"
                  />
                </div>
              ) : (
                <div className="border-2 border-dashed border-[#333] rounded-lg p-8 bg-[#1a1a1a] flex items-center justify-center">
                  <span className="text-[#666666] text-sm">Kein Logo hochgeladen</span>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  onClick={() => openFileUpload('logo_dark_url')}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Upload size={16} />
                  Hochladen
                </Button>
                {settings.logo_dark_url && (
                  <Button
                    type="button"
                    onClick={() => handleChange('logo_dark_url', null)}
                    className="text-red-500 hover:text-red-700 flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    Entfernen
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Vorschau</h3>
            <p className="text-sm text-[#666666] mb-4">
              So wird das Logo/der Name in verschiedenen Bereichen angezeigt:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Light Mode Preview */}
              <div className="border border-[#E5E5E5] rounded-lg p-4 bg-white">
                <p className="text-xs text-[#999999] uppercase tracking-wider mb-3">Light Mode</p>
                <div className="flex items-center">
                  {settings.logo_light_url ? (
                    <img 
                      src={getImageUrl(settings.logo_light_url)} 
                      alt="Logo" 
                      className="h-10 object-contain"
                    />
                  ) : (
                    <span className="font-heading text-xl font-bold tracking-tight">
                      <span style={{ fontFamily: "'Poppins', sans-serif" }}>NYZA-STUDIO</span>{' '}
                      <span className="text-gold">CARDEALER</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Dark Mode Preview */}
              <div className="border border-[#333] rounded-lg p-4 bg-[#0A0A0A]">
                <p className="text-xs text-[#666666] uppercase tracking-wider mb-3">Dark Mode</p>
                <div className="flex items-center">
                  {settings.logo_dark_url ? (
                    <img 
                      src={getImageUrl(settings.logo_dark_url)} 
                      alt="Logo" 
                      className="h-10 object-contain"
                    />
                  ) : (
                    <span className="font-heading text-xl font-bold tracking-tight">
                      <span style={{ fontFamily: "'Poppins', sans-serif", color: '#FFFFFF' }}>NYZA-STUDIO</span>{' '}
                      <span className="text-gold">CARDEALER</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>💡 Hinweis:</strong> Wenn kein Logo hochgeladen ist, wird automatisch der Schriftzug "NYZA-STUDIO CARDEALER" 
              in einer eleganten Schriftart angezeigt. Das Logo wird im Admin-Bereich, im Header und im Footer verwendet.
            </p>
          </div>
        </div>
      )}

      {/* Featured Cars Settings */}
      {moduleId === 'featured' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Überschriften</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">
                  Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.featured_title_en || '') : (settings.featured_title || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'featured_title_en' : 'featured_title', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Featured Vehicles' : 'Ausgewählte Fahrzeuge'}
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Untertitel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.featured_subtitle_en || '') : (settings.featured_subtitle || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'featured_subtitle_en' : 'featured_subtitle', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Premium Selection' : 'Premium Auswahl'}
                />
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Anzeige-Optionen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Anzahl Fahrzeuge</label>
                <input
                  type="number"
                  value={settings.featured_count || 6}
                  onChange={(e) => handleChange('featured_count', parseInt(e.target.value))}
                  className="input-premium w-full"
                  min="3"
                  max="12"
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  "Alle anzeigen" Text {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.featured_view_all_en || '') : (settings.featured_view_all || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'featured_view_all_en' : 'featured_view_all', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'View All' : 'Alle anzeigen'}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* About Section Settings */}
      {moduleId === 'about' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Überschriften</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">
                  Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.about_title_en || '') : (settings.about_title || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'about_title_en' : 'about_title', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'About Us' : 'Über uns'}
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Untertitel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.about_subtitle_en || '') : (settings.about_subtitle || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'about_subtitle_en' : 'about_subtitle', e.target.value)}
                  className="input-premium w-full"
                />
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Inhalt</h3>
            <div className="space-y-4">
              <div>
                <label className="overline block mb-2">
                  Text {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <textarea
                  value={editLang === 'en' ? (settings.about_text_en || '') : (settings.about_text || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'about_text_en' : 'about_text', e.target.value)}
                  className="input-premium w-full h-32 resize-none"
                  placeholder={editLang === 'en' ? 'About us text...' : 'Über uns Text...'}
                />
              </div>
              <div>
                <label className="overline block mb-2">Bild-URL</label>
                <input
                  type="text"
                  value={settings.about_image_url || ''}
                  onChange={(e) => handleChange('about_image_url', e.target.value)}
                  className="input-premium w-full"
                  placeholder="https://..."
                />
                {settings.about_image_url && (
                  <img 
                    src={getImageUrl(settings.about_image_url)} 
                    alt="Preview" 
                    className="mt-2 h-32 object-cover rounded-lg"
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Statistics Section */}
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Statistiken</h3>
            <p className="text-sm text-[#666666] mb-4">
              Diese Zahlen werden im "Über uns"-Bereich auf der Startseite angezeigt.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Stat 1: Years */}
              <div className="p-4 bg-[#F9F9F7] rounded-lg">
                <label className="overline block mb-2">Zahl 1 (z.B. Jahre)</label>
                <input
                  type="text"
                  value={settings.stat_years || ''}
                  onChange={(e) => handleChange('stat_years', e.target.value)}
                  className="input-premium w-full mb-2"
                  placeholder="20+"
                />
                <label className="text-xs text-[#666666] mb-1 block">
                  Bezeichnung {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.stat_years_label_en || '') : (settings.stat_years_label || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'stat_years_label_en' : 'stat_years_label', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Years Experience' : 'Jahre Erfahrung'}
                />
              </div>
              
              {/* Stat 2: Cars */}
              <div className="p-4 bg-[#F9F9F7] rounded-lg">
                <label className="overline block mb-2">Zahl 2 (z.B. Fahrzeuge)</label>
                <input
                  type="text"
                  value={settings.stat_cars || ''}
                  onChange={(e) => handleChange('stat_cars', e.target.value)}
                  className="input-premium w-full mb-2"
                  placeholder="500+"
                />
                <label className="text-xs text-[#666666] mb-1 block">
                  Bezeichnung {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.stat_cars_label_en || '') : (settings.stat_cars_label || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'stat_cars_label_en' : 'stat_cars_label', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Cars Sold' : 'Verkaufte Autos'}
                />
              </div>
              
              {/* Stat 3: Satisfaction */}
              <div className="p-4 bg-[#F9F9F7] rounded-lg">
                <label className="overline block mb-2">Zahl 3 (z.B. Zufriedenheit)</label>
                <input
                  type="text"
                  value={settings.stat_satisfaction || ''}
                  onChange={(e) => handleChange('stat_satisfaction', e.target.value)}
                  className="input-premium w-full mb-2"
                  placeholder="100%"
                />
                <label className="text-xs text-[#666666] mb-1 block">
                  Bezeichnung {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.stat_satisfaction_label_en || '') : (settings.stat_satisfaction_label || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'stat_satisfaction_label_en' : 'stat_satisfaction_label', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Satisfaction' : 'Zufriedenheit'}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Section Settings */}
      {moduleId === 'video' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Überschriften</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">
                  Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.video_title_en || '') : (settings.video_title || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'video_title_en' : 'video_title', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'The Art of Driving' : 'Die Kunst des Fahrens'}
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Untertitel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.video_subtitle_en || '') : (settings.video_subtitle || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'video_subtitle_en' : 'video_subtitle', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Experience' : 'Erleben'}
                />
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Video & Thumbnail</h3>
            <div className="space-y-4">
              <div>
                <label className="overline block mb-2">Video-URL (YouTube/Vimeo)</label>
                <input
                  type="text"
                  value={settings.video_url || ''}
                  onChange={(e) => handleChange('video_url', e.target.value)}
                  className="input-premium w-full"
                  placeholder="https://youtube.com/watch?v=..."
                />
                <p className="text-xs text-[#666666] mt-1">
                  Das YouTube-Thumbnail wird automatisch verwendet.
                </p>
              </div>
              <div>
                <label className="overline block mb-2">Eigenes Thumbnail (optional)</label>
                <input
                  type="text"
                  value={settings.video_thumbnail_url || ''}
                  onChange={(e) => handleChange('video_thumbnail_url', e.target.value)}
                  className="input-premium w-full"
                  placeholder="Leer lassen für automatisches YouTube-Thumbnail"
                />
                {settings.video_thumbnail_url && (
                  <div className="mt-2 relative aspect-video max-w-md rounded-lg overflow-hidden">
                    <img 
                      src={getImageUrl(settings.video_thumbnail_url)} 
                      alt="Video Thumbnail" 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <Play size={20} className="text-white ml-1" fill="white" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Video Background Style */}
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Hintergrund-Stil</h3>
            <p className="text-sm text-[#666666] mb-4">
              Wählen Sie, ob der Video-Bereich einen dunklen oder hellen Hintergrund haben soll. Im hellen Modus wird der Hintergrund automatisch angepasst.
            </p>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="video_bg_style"
                  checked={settings.video_light_mode !== true}
                  onChange={() => handleChange('video_light_mode', false)}
                  className="w-4 h-4 accent-primary"
                />
                <span className="text-sm">Immer dunkel</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="video_bg_style"
                  checked={settings.video_light_mode === true}
                  onChange={() => handleChange('video_light_mode', true)}
                  className="w-4 h-4 accent-primary"
                />
                <span className="text-sm">Theme-abhängig (hell/dunkel)</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Settings */}
      {moduleId === 'newsletter' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Überschriften</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">
                  Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.newsletter_title_en || '') : (settings.newsletter_title || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'newsletter_title_en' : 'newsletter_title', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Stay Updated' : 'Bleiben Sie informiert'}
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Untertitel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.newsletter_subtitle_en || '') : (settings.newsletter_subtitle || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'newsletter_subtitle_en' : 'newsletter_subtitle', e.target.value)}
                  className="input-premium w-full"
                />
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Formular-Texte</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">
                  Beschreibung {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <textarea
                  value={editLang === 'en' ? (settings.newsletter_text_en || '') : (settings.newsletter_text || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'newsletter_text_en' : 'newsletter_text', e.target.value)}
                  className="input-premium w-full h-24 resize-none"
                  placeholder={editLang === 'en' ? 'Subscribe to receive...' : 'Abonnieren Sie...'}
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Button-Text {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.newsletter_button_en || '') : (settings.newsletter_button || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'newsletter_button_en' : 'newsletter_button', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Subscribe' : 'Abonnieren'}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Settings */}
      {moduleId === 'contact' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Überschriften</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">
                  Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.contact_title_en || '') : (settings.contact_title || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'contact_title_en' : 'contact_title', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Contact Us' : 'Kontakt'}
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Untertitel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.contact_subtitle_en || '') : (settings.contact_subtitle || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'contact_subtitle_en' : 'contact_subtitle', e.target.value)}
                  className="input-premium w-full"
                />
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Kontaktdaten</h3>
            <p className="text-sm text-[#666666] mb-4">
              Diese Daten werden im Footer und auf der Kontaktseite angezeigt.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Adresse</label>
                <input
                  type="text"
                  value={settings.contact_address || ''}
                  onChange={(e) => handleChange('contact_address', e.target.value)}
                  className="input-premium w-full"
                  placeholder="Musterstraße 123, 1010 Wien"
                />
              </div>
              <div>
                <label className="overline block mb-2">Telefon</label>
                <input
                  type="text"
                  value={settings.contact_phone || ''}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  className="input-premium w-full"
                  placeholder="+43 1 234 5678"
                />
              </div>
              <div>
                <label className="overline block mb-2">E-Mail</label>
                <input
                  type="email"
                  value={settings.contact_email || ''}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  className="input-premium w-full"
                  placeholder="kontakt@ihre-domain.at"
                  data-testid="contact-email-module"
                />
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ Anfragen werden an diese E-Mail gesendet (wenn Chat deaktiviert)
                </p>
              </div>
              <div>
                <label className="overline block mb-2">Öffnungszeiten</label>
                <input
                  type="text"
                  value={settings.contact_hours || ''}
                  onChange={(e) => handleChange('contact_hours', e.target.value)}
                  className="input-premium w-full"
                  placeholder="Mo-Fr: 9-18 Uhr"
                />
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">
              Beschreibungstext {editLang === 'en' && <span className="text-blue-600 text-sm">(EN)</span>}
            </h3>
            <textarea
              value={editLang === 'en' ? (settings.contact_text_en || '') : (settings.contact_text || '')}
              onChange={(e) => handleChange(editLang === 'en' ? 'contact_text_en' : 'contact_text', e.target.value)}
              className="input-premium w-full h-24 resize-none"
              placeholder={editLang === 'en' ? 'Contact us for...' : 'Kontaktieren Sie uns für...'}
            />
          </div>
        </div>
      )}

      {/* FAQ Settings */}
      {moduleId === 'faq' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Allgemeine Einstellungen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">
                  Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.faq_title_en || '') : (settings.faq_title || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'faq_title_en' : 'faq_title', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'FAQ' : 'Häufige Fragen'}
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Untertitel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.faq_subtitle_en || '') : (settings.faq_subtitle || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'faq_subtitle_en' : 'faq_subtitle', e.target.value)}
                  className="input-premium w-full"
                />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h3 className="font-heading text-lg">Fragen & Antworten</h3>
              <Button
                type="button"
                onClick={() => {
                  const newItems = [...(settings.faq_items || []), { question: '', question_en: '', answer: '', answer_en: '' }];
                  handleChange('faq_items', newItems);
                }}
                className="btn-secondary flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <Plus size={16} />
                Frage hinzufügen
              </Button>
            </div>
            
            <div className="space-y-4">
              {(settings.faq_items || []).map((item, index) => (
                <div key={index} className="p-3 md:p-4 bg-[#F9F9F7] rounded-lg border border-[#E5E5E5]">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <GripVertical size={16} className="text-[#999999] cursor-grab hidden sm:block" />
                      <span className="text-sm font-medium">Frage {index + 1}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = (settings.faq_items || []).filter((_, i) => i !== index);
                        handleChange('faq_items', newItems);
                      }}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-[#666666] mb-1 block">
                        Frage {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                      </label>
                      <input
                        type="text"
                        value={editLang === 'en' ? (item.question_en || '') : (item.question || '')}
                        onChange={(e) => {
                          const newItems = [...(settings.faq_items || [])];
                          newItems[index] = { ...newItems[index], [editLang === 'en' ? 'question_en' : 'question']: e.target.value };
                          handleChange('faq_items', newItems);
                        }}
                        className="input-premium w-full"
                        placeholder={editLang === 'en' ? 'Question' : 'Frage eingeben...'}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#666666] mb-1 block">
                        Antwort {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                      </label>
                      <textarea
                        value={editLang === 'en' ? (item.answer_en || '') : (item.answer || '')}
                        onChange={(e) => {
                          const newItems = [...(settings.faq_items || [])];
                          newItems[index] = { ...newItems[index], [editLang === 'en' ? 'answer_en' : 'answer']: e.target.value };
                          handleChange('faq_items', newItems);
                        }}
                        className="input-premium w-full h-20 md:h-24 resize-none"
                        placeholder={editLang === 'en' ? 'Answer' : 'Antwort eingeben...'}
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {(settings.faq_items || []).length === 0 && (
                <div className="text-center py-8 text-[#999999]">
                  <HelpCircle size={32} className="mx-auto mb-2 opacity-30" />
                  <p>Noch keine Fragen hinzugefügt</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Brands Settings */}
      {moduleId === 'brands' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Allgemeine Einstellungen</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="overline block mb-2">
                  Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.brands_title_en || '') : (settings.brands_title || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'brands_title_en' : 'brands_title', e.target.value)}
                  className="input-premium w-full"
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Untertitel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.brands_subtitle_en || '') : (settings.brands_subtitle || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'brands_subtitle_en' : 'brands_subtitle', e.target.value)}
                  className="input-premium w-full"
                />
              </div>
              <div>
                <label className="overline block mb-2">Scroll-Geschwindigkeit</label>
                <input
                  type="number"
                  value={settings.brands_scroll_speed || 30}
                  onChange={(e) => handleChange('brands_scroll_speed', parseInt(e.target.value))}
                  className="input-premium w-full"
                  min="10"
                  max="100"
                />
                <p className="text-xs text-[#999999] mt-1">Sekunden für eine Runde</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Marken-Logos</h3>
            <p className="text-sm text-[#666666] mb-4">
              Sie können für jedes Logo eine helle und dunkle Version hochladen. Wenn nur eine Version vorhanden ist, wird diese in beiden Modi verwendet.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {(settings.brands_logos || []).map((logo, index) => (
                <div key={index} className="relative group border border-[#E5E5E5] rounded-lg p-4">
                  <div className="flex gap-3 mb-3">
                    {/* Light Logo */}
                    <div className="flex-1">
                      <p className="text-xs text-[#666666] mb-1">Hell</p>
                      <div className="aspect-[2/1] bg-white rounded flex items-center justify-center p-2 border border-[#E5E5E5]">
                        {logo.url ? (
                          <img src={getImageUrl(logo.url)} alt={logo.name || ''} className="max-h-full max-w-full object-contain" />
                        ) : (
                          <span className="text-xs text-[#999999]">-</span>
                        )}
                      </div>
                    </div>
                    {/* Dark Logo */}
                    <div className="flex-1">
                      <p className="text-xs text-[#666666] mb-1">Dunkel</p>
                      <div className="aspect-[2/1] bg-[#1A1A1A] rounded flex items-center justify-center p-2 border border-[#333333]">
                        {logo.url_dark ? (
                          <img src={getImageUrl(logo.url_dark)} alt={logo.name || ''} className="max-h-full max-w-full object-contain" />
                        ) : logo.url ? (
                          <img src={getImageUrl(logo.url)} alt={logo.name || ''} className="max-h-full max-w-full object-contain opacity-50" />
                        ) : (
                          <span className="text-xs text-[#666666]">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-center text-[#666666] truncate">{logo.name || `Logo ${index + 1}`}</p>
                  <button
                    type="button"
                    onClick={() => {
                      const newLogos = (settings.brands_logos || []).filter((_, i) => i !== index);
                      handleChange('brands_logos', newLogos);
                    }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>

            <div className="p-4 bg-[#F9F9F7] rounded-lg">
              <h4 className="text-sm font-medium mb-3">Neues Logo hinzufügen</h4>
              <div className="grid grid-cols-1 gap-3">
                <input
                  type="text"
                  id="new-brand-name"
                  placeholder="Markenname"
                  className="input-premium"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#666666] mb-1 block">Logo (Hell / Standard)</label>
                    <input
                      type="text"
                      id="new-brand-url"
                      placeholder="Logo-URL (https://...)"
                      className="input-premium"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#666666] mb-1 block">Logo (Dunkel) - optional</label>
                    <input
                      type="text"
                      id="new-brand-url-dark"
                      placeholder="Dunkle Version URL (optional)"
                      className="input-premium"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={() => {
                    const name = document.getElementById('new-brand-name').value;
                    const url = document.getElementById('new-brand-url').value;
                    const urlDark = document.getElementById('new-brand-url-dark').value;
                    if (url) {
                      const newLogo = { name, url };
                      if (urlDark) newLogo.url_dark = urlDark;
                      const newLogos = [...(settings.brands_logos || []), newLogo];
                      handleChange('brands_logos', newLogos);
                      document.getElementById('new-brand-name').value = '';
                      document.getElementById('new-brand-url').value = '';
                      document.getElementById('new-brand-url-dark').value = '';
                    }
                  }}
                  className="btn-secondary flex items-center justify-center gap-2"
                >
                  <Plus size={16} />
                  Hinzufügen
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Services Settings */}
      {moduleId === 'services' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Allgemeine Einstellungen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">
                  Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.services_title_en || '') : (settings.services_title || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'services_title_en' : 'services_title', e.target.value)}
                  className="input-premium w-full"
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Untertitel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.services_subtitle_en || '') : (settings.services_subtitle || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'services_subtitle_en' : 'services_subtitle', e.target.value)}
                  className="input-premium w-full"
                />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h3 className="font-heading text-lg">Services</h3>
              <Button
                type="button"
                onClick={() => {
                  const newItems = [...(settings.services_items || []), { title: '', title_en: '', description: '', description_en: '', icon: 'shield', icon_url: '' }];
                  handleChange('services_items', newItems);
                }}
                className="btn-secondary flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <Plus size={16} />
                Service hinzufügen
              </Button>
            </div>
            
            <div className="space-y-4">
              {(settings.services_items || []).map((item, index) => (
                <div key={index} className="p-3 md:p-4 bg-[#F9F9F7] rounded-lg border border-[#E5E5E5]">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <GripVertical size={16} className="text-[#999999] cursor-grab hidden sm:block" />
                      <span className="text-sm font-medium">Service {index + 1}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = (settings.services_items || []).filter((_, i) => i !== index);
                        handleChange('services_items', newItems);
                      }}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#666666] mb-1 block">
                        Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                      </label>
                      <input
                        type="text"
                        value={editLang === 'en' ? (item.title_en || '') : (item.title || '')}
                        onChange={(e) => {
                          const newItems = [...(settings.services_items || [])];
                          newItems[index] = { ...newItems[index], [editLang === 'en' ? 'title_en' : 'title']: e.target.value };
                          handleChange('services_items', newItems);
                        }}
                        className="input-premium w-full"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#666666] mb-1 block">
                        Beschreibung {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                      </label>
                      <input
                        type="text"
                        value={editLang === 'en' ? (item.description_en || '') : (item.description || '')}
                        onChange={(e) => {
                          const newItems = [...(settings.services_items || [])];
                          newItems[index] = { ...newItems[index], [editLang === 'en' ? 'description_en' : 'description']: e.target.value };
                          handleChange('services_items', newItems);
                        }}
                        className="input-premium w-full"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#666666] mb-1 block">Icon-Typ</label>
                      <select
                        value={item.icon || 'shield'}
                        onChange={(e) => {
                          const newItems = [...(settings.services_items || [])];
                          newItems[index] = { ...newItems[index], icon: e.target.value };
                          handleChange('services_items', newItems);
                        }}
                        className="input-premium w-full"
                      >
                        <option value="shield-check">Prüfung</option>
                        <option value="shield">Schutz</option>
                        <option value="circle">Reifen</option>
                        <option value="credit-card">Finanzierung</option>
                        <option value="car">Auto</option>
                        <option value="wrench">Werkzeug</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#666666] mb-1 block">Icon-URL (optional)</label>
                      <input
                        type="text"
                        value={item.icon_url || ''}
                        onChange={(e) => {
                          const newItems = [...(settings.services_items || [])];
                          newItems[index] = { ...newItems[index], icon_url: e.target.value };
                          handleChange('services_items', newItems);
                        }}
                        className="input-premium w-full"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {(settings.services_items || []).length === 0 && (
                <div className="text-center py-8 text-[#999999]">
                  <Wrench size={32} className="mx-auto mb-2 opacity-30" />
                  <p>Noch keine Services hinzugefügt</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Team Settings */}
      {moduleId === 'team' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Allgemeine Einstellungen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">
                  Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.team_title_en || '') : (settings.team_title || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'team_title_en' : 'team_title', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Our Team' : 'Unser Team'}
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Untertitel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.team_subtitle_en || '') : (settings.team_subtitle || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'team_subtitle_en' : 'team_subtitle', e.target.value)}
                  className="input-premium w-full"
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Navigation-Text {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.nav_team_en || '') : (settings.nav_team || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'nav_team_en' : 'nav_team', e.target.value)}
                  className="input-premium w-full"
                  placeholder="Team"
                />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h3 className="font-heading text-lg">Teammitglieder</h3>
              <Button
                type="button"
                onClick={() => {
                  const newMembers = [...(settings.team_members || []), { 
                    id: Date.now().toString(),
                    name: '', 
                    position: '', 
                    position_en: '', 
                    email: '', 
                    phone: '', 
                    image_url: '',
                    order: (settings.team_members || []).length
                  }];
                  handleChange('team_members', newMembers);
                }}
                className="btn-secondary flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <Plus size={16} />
                Mitglied hinzufügen
              </Button>
            </div>
            
            <div className="space-y-4">
              {(settings.team_members || []).map((member, index) => (
                <div key={member.id || index} className="p-3 md:p-4 bg-[#F9F9F7] rounded-lg border border-[#E5E5E5]">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center text-sm font-medium">{index + 1}</span>
                      <span className="text-sm font-medium">{member.name || 'Neues Mitglied'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newMembers = (settings.team_members || []).filter((_, i) => i !== index);
                        handleChange('team_members', newMembers);
                      }}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#666666] mb-1 block">Name</label>
                      <input
                        type="text"
                        value={member.name || ''}
                        onChange={(e) => {
                          const newMembers = [...(settings.team_members || [])];
                          newMembers[index] = { ...newMembers[index], name: e.target.value };
                          handleChange('team_members', newMembers);
                        }}
                        className="input-premium w-full"
                        placeholder="Max Mustermann"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#666666] mb-1 block">
                        Position {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                      </label>
                      <input
                        type="text"
                        value={editLang === 'en' ? (member.position_en || '') : (member.position || '')}
                        onChange={(e) => {
                          const newMembers = [...(settings.team_members || [])];
                          newMembers[index] = { ...newMembers[index], [editLang === 'en' ? 'position_en' : 'position']: e.target.value };
                          handleChange('team_members', newMembers);
                        }}
                        className="input-premium w-full"
                        placeholder={editLang === 'en' ? 'Sales Manager' : 'Verkaufsleiter'}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#666666] mb-1 block">E-Mail</label>
                      <input
                        type="email"
                        value={member.email || ''}
                        onChange={(e) => {
                          const newMembers = [...(settings.team_members || [])];
                          newMembers[index] = { ...newMembers[index], email: e.target.value };
                          handleChange('team_members', newMembers);
                        }}
                        className="input-premium w-full"
                        placeholder="max@beispiel.de"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#666666] mb-1 block">Telefon</label>
                      <input
                        type="text"
                        value={member.phone || ''}
                        onChange={(e) => {
                          const newMembers = [...(settings.team_members || [])];
                          newMembers[index] = { ...newMembers[index], phone: e.target.value };
                          handleChange('team_members', newMembers);
                        }}
                        className="input-premium w-full"
                        placeholder="+43 1 234 5678"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs text-[#666666] mb-1 block">Foto</label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={member.image_url || ''}
                          onChange={(e) => {
                            const newMembers = [...(settings.team_members || [])];
                            newMembers[index] = { ...newMembers[index], image_url: e.target.value };
                            handleChange('team_members', newMembers);
                          }}
                          className="input-premium flex-1"
                          placeholder="URL eingeben oder Bild hochladen..."
                        />
                        <label className="btn-secondary flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
                          <Upload size={16} />
                          <span>Hochladen</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              
                              const formData = new FormData();
                              formData.append('file', file);
                              
                              try {
                                const token = localStorage.getItem('token');
                                const res = await fetch(`${API_URL}/api/media/upload`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}` },
                                  body: formData
                                });
                                
                                if (res.ok) {
                                  const data = await res.json();
                                  const newMembers = [...(settings.team_members || [])];
                                  newMembers[index] = { ...newMembers[index], image_url: data.url };
                                  handleChange('team_members', newMembers);
                                  toast.success('Bild hochgeladen');
                                } else {
                                  toast.error('Fehler beim Hochladen');
                                }
                              } catch (error) {
                                toast.error('Fehler beim Hochladen');
                              }
                            }}
                          />
                        </label>
                      </div>
                      {member.image_url && (
                        <div className="mt-2 relative w-20 h-20 rounded-lg overflow-hidden bg-[#E5E5E5]">
                          <img 
                            src={member.image_url.startsWith('http') ? member.image_url : `${API_URL}${member.image_url}`} 
                            alt={member.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {(settings.team_members || []).length === 0 && (
                <div className="text-center py-8 text-[#999999]">
                  <Users size={32} className="mx-auto mb-2 opacity-30" />
                  <p>Noch keine Teammitglieder hinzugefügt</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Blog Settings */}
      {moduleId === 'blog' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Blog-Einstellungen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">
                  Seiten-Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.blog_title_en || '') : (settings.blog_title || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'blog_title_en' : 'blog_title', e.target.value)}
                  className="input-premium w-full"
                  placeholder="News & Blog"
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Untertitel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.blog_subtitle_en || '') : (settings.blog_subtitle || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'blog_subtitle_en' : 'blog_subtitle', e.target.value)}
                  className="input-premium w-full"
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Navigation-Text {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={editLang === 'en' ? (settings.nav_blog_en || '') : (settings.nav_blog || '')}
                  onChange={(e) => handleChange(editLang === 'en' ? 'nav_blog_en' : 'nav_blog', e.target.value)}
                  className="input-premium w-full"
                  placeholder="News"
                />
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Hinweis:</strong> Blog-Beiträge verwalten Sie unter <a href="/admin/blog" className="underline font-medium">Admin → Blog</a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Social Media Settings */}
      {moduleId === 'social' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-2">Social Media Links</h3>
            <p className="text-sm text-[#666666] mb-6">
              Nur ausgefüllte Links werden im Footer angezeigt.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="overline block mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Facebook
                </label>
                <input
                  type="url"
                  value={settings.social_facebook || ''}
                  onChange={(e) => handleChange('social_facebook', e.target.value)}
                  className="input-premium w-full"
                  placeholder="https://facebook.com/..."
                />
              </div>
              
              <div>
                <label className="overline block mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  Instagram
                </label>
                <input
                  type="url"
                  value={settings.social_instagram || ''}
                  onChange={(e) => handleChange('social_instagram', e.target.value)}
                  className="input-premium w-full"
                  placeholder="https://instagram.com/..."
                />
              </div>
              
              <div>
                <label className="overline block mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                  TikTok
                </label>
                <input
                  type="url"
                  value={settings.social_tiktok || ''}
                  onChange={(e) => handleChange('social_tiktok', e.target.value)}
                  className="input-premium w-full"
                  placeholder="https://tiktok.com/@..."
                />
              </div>
              
              <div>
                <label className="overline block mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  YouTube
                </label>
                <input
                  type="url"
                  value={settings.social_youtube || ''}
                  onChange={(e) => handleChange('social_youtube', e.target.value)}
                  className="input-premium w-full"
                  placeholder="https://youtube.com/..."
                />
              </div>
              
              <div>
                <label className="overline block mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  LinkedIn
                </label>
                <input
                  type="url"
                  value={settings.social_linkedin || ''}
                  onChange={(e) => handleChange('social_linkedin', e.target.value)}
                  className="input-premium w-full"
                  placeholder="https://linkedin.com/company/..."
                />
              </div>
              
              <div>
                <label className="overline block mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg>
                  X (Twitter)
                </label>
                <input
                  type="url"
                  value={settings.social_x || ''}
                  onChange={(e) => handleChange('social_x', e.target.value)}
                  className="input-premium w-full"
                  placeholder="https://x.com/..."
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Drive Settings */}
      {moduleId === 'test-drive' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-2">Probefahrt Einstellungen</h3>
            <p className="text-sm text-[#666666] mb-6">
              Konfigurieren Sie die Probefahrt-Buchungsfunktion.
            </p>
            
            <div className="space-y-4">
              {/* Notification Email */}
              <div>
                <label className="overline block mb-2">Benachrichtigungs-E-Mail</label>
                <input
                  type="email"
                  value={settings.test_drive_notification_email || ''}
                  onChange={(e) => handleChange('test_drive_notification_email', e.target.value)}
                  className="input-premium w-full"
                  placeholder="noreply@nyza-studio.at"
                />
                <p className="text-xs text-[#999999] mt-1">
                  An diese Adresse werden Benachrichtigungen über neue Probefahrt-Anfragen gesendet. Standard: noreply@nyza-studio.at
                </p>
              </div>

              {/* Navigation Text */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">
                    Menü-Text {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                  </label>
                  <input
                    type="text"
                    value={editLang === 'en' ? (settings.module_test_drive_nav_text_en || '') : (settings.module_test_drive_nav_text || '')}
                    onChange={(e) => handleChange(editLang === 'en' ? 'module_test_drive_nav_text_en' : 'module_test_drive_nav_text', e.target.value)}
                    className="input-premium w-full"
                    placeholder={editLang === 'en' ? 'Test Drive' : 'Probefahrt'}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Zeit-Slot Dauer (Minuten)</label>
                  <select
                    value={settings.test_drive_slot_duration || 60}
                    onChange={(e) => handleChange('test_drive_slot_duration', parseInt(e.target.value))}
                    className="input-premium w-full"
                  >
                    <option value={30}>30 Minuten</option>
                    <option value={60}>60 Minuten</option>
                  </select>
                </div>
              </div>

              {/* Test Drive Hours per Weekday */}
              <div>
                <h4 className="font-medium mb-3">Probefahrt-Zeiten pro Wochentag</h4>
                <p className="text-sm text-[#666666] mb-4">
                  Definieren Sie für jeden Wochentag die verfügbaren Zeiten für Probefahrten.
                </p>
                <div className="space-y-3">
                  {['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'].map((day, index) => {
                    const dayKey = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][index];
                    const hours = settings.test_drive_hours || {};
                    const dayHours = hours[dayKey] || { enabled: index < 6, start: '09:00', end: '18:00' };
                    
                    return (
                      <div key={dayKey} className="flex items-center gap-4 p-3 bg-[#F9F9F7] rounded-lg">
                        <label className="flex items-center gap-2 w-32">
                          <input
                            type="checkbox"
                            checked={dayHours.enabled}
                            onChange={(e) => {
                              const newHours = { ...hours };
                              newHours[dayKey] = { ...dayHours, enabled: e.target.checked };
                              handleChange('test_drive_hours', newHours);
                            }}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <span className="text-sm font-medium">{day}</span>
                        </label>
                        {dayHours.enabled && (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="time"
                              value={dayHours.start}
                              onChange={(e) => {
                                const newHours = { ...hours };
                                newHours[dayKey] = { ...dayHours, start: e.target.value };
                                handleChange('test_drive_hours', newHours);
                              }}
                              className="input-premium text-sm px-2 py-1"
                            />
                            <span className="text-sm text-[#666666]">bis</span>
                            <input
                              type="time"
                              value={dayHours.end}
                              onChange={(e) => {
                                const newHours = { ...hours };
                                newHours[dayKey] = { ...dayHours, end: e.target.value };
                                handleChange('test_drive_hours', newHours);
                              }}
                              className="input-premium text-sm px-2 py-1"
                            />
                          </div>
                        )}
                        {!dayHours.enabled && (
                          <span className="text-sm text-[#999999]">Geschlossen</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Financing Settings */}
      {moduleId === 'financing' && (
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Grundeinstellungen</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">Zinssatz (% p.a.)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="30"
                  value={settings.financing_interest_rate ?? 4.9}
                  onChange={(e) => handleChange('financing_interest_rate', parseFloat(e.target.value))}
                  className="input-premium w-full"
                  data-testid="financing-interest-input"
                />
              </div>
              <div>
                <label className="overline block mb-2">Standard-Anzahlung (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.financing_default_down_payment_percent ?? 20}
                  onChange={(e) => handleChange('financing_default_down_payment_percent', parseInt(e.target.value))}
                  className="input-premium w-full"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="overline block mb-2">Laufzeiten (Monate)</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {(settings.financing_months || [6, 12, 24, 36, 48, 60]).map((month) => (
                  <span
                    key={month}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-[#F9F9F7] border border-[#E5E5E5] rounded"
                  >
                    {month} Monate
                    <button
                      type="button"
                      onClick={() => {
                        const newMonths = (settings.financing_months || [6, 12, 24, 36, 48, 60]).filter(m => m !== month);
                        handleChange('financing_months', newMonths);
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Neue Laufzeit"
                  min="1"
                  max="120"
                  className="input-premium w-32"
                  id="new-month-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const value = parseInt(e.target.value);
                      if (value && value > 0) {
                        const currentMonths = settings.financing_months || [6, 12, 24, 36, 48, 60];
                        if (!currentMonths.includes(value)) {
                          handleChange('financing_months', [...currentMonths, value].sort((a, b) => a - b));
                        }
                        e.target.value = '';
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const input = document.getElementById('new-month-input');
                    const value = parseInt(input.value);
                    if (value && value > 0) {
                      const currentMonths = settings.financing_months || [6, 12, 24, 36, 48, 60];
                      if (!currentMonths.includes(value)) {
                        handleChange('financing_months', [...currentMonths, value].sort((a, b) => a - b));
                      }
                      input.value = '';
                    }
                  }}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Plus size={16} />
                  Hinzufügen
                </button>
              </div>
            </div>

            <div className="mt-6">
              <label className="overline block mb-2">Partnerbank-Text</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-[#999999]">🇩🇪 Deutsch</span>
                  </div>
                  <input
                    type="text"
                    value={settings.financing_partner_text || ''}
                    onChange={(e) => handleChange('financing_partner_text', e.target.value)}
                    className="input-premium w-full"
                    placeholder="In Kooperation mit unserer Partnerbank Santander"
                    data-testid="financing-partner-input"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-[#999999]">🇬🇧 English</span>
                  </div>
                  <input
                    type="text"
                    value={settings.financing_partner_text_en || ''}
                    onChange={(e) => handleChange('financing_partner_text_en', e.target.value)}
                    className="input-premium w-full"
                    placeholder="In cooperation with our partner bank Santander"
                  />
                </div>
              </div>
              <p className="text-xs text-[#999999] mt-1">Wird unter dem Finanzierungsrechner angezeigt</p>
            </div>
          </div>

          {/* Financing Labels Multi-Language */}
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Beschriftungen</h3>
            <p className="text-sm text-[#666666] mb-6">
              Passen Sie die Beschriftungen des Finanzierungsrechners für beide Sprachen an.
            </p>
            
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="overline block mb-2">Überschrift</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#999999]">🇩🇪 Deutsch</span>
                    </div>
                    <input
                      type="text"
                      value={settings.financing_title || 'Finanzierung'}
                      onChange={(e) => handleChange('financing_title', e.target.value)}
                      className="input-premium w-full"
                      placeholder="Finanzierung"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#999999]">🇬🇧 English</span>
                    </div>
                    <input
                      type="text"
                      value={settings.financing_title_en || 'Financing'}
                      onChange={(e) => handleChange('financing_title_en', e.target.value)}
                      className="input-premium w-full"
                      placeholder="Financing"
                    />
                  </div>
                </div>
              </div>

              {/* Down Payment Label */}
              <div>
                <label className="overline block mb-2">Anzahlung-Label</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#999999]">🇩🇪 Deutsch</span>
                    </div>
                    <input
                      type="text"
                      value={settings.financing_down_payment_label || 'Anzahlung'}
                      onChange={(e) => handleChange('financing_down_payment_label', e.target.value)}
                      className="input-premium w-full"
                      placeholder="Anzahlung"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#999999]">🇬🇧 English</span>
                    </div>
                    <input
                      type="text"
                      value={settings.financing_down_payment_label_en || 'Down Payment'}
                      onChange={(e) => handleChange('financing_down_payment_label_en', e.target.value)}
                      className="input-premium w-full"
                      placeholder="Down Payment"
                    />
                  </div>
                </div>
              </div>

              {/* Term Label */}
              <div>
                <label className="overline block mb-2">Laufzeit-Label</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#999999]">🇩🇪 Deutsch</span>
                    </div>
                    <input
                      type="text"
                      value={settings.financing_term_label || 'Laufzeit'}
                      onChange={(e) => handleChange('financing_term_label', e.target.value)}
                      className="input-premium w-full"
                      placeholder="Laufzeit"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#999999]">🇬🇧 English</span>
                    </div>
                    <input
                      type="text"
                      value={settings.financing_term_label_en || 'Term'}
                      onChange={(e) => handleChange('financing_term_label_en', e.target.value)}
                      className="input-premium w-full"
                      placeholder="Term"
                    />
                  </div>
                </div>
              </div>

              {/* Monthly Payment Label */}
              <div>
                <label className="overline block mb-2">Monatliche Rate-Label</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#999999]">🇩🇪 Deutsch</span>
                    </div>
                    <input
                      type="text"
                      value={settings.financing_monthly_label || 'Monatliche Rate'}
                      onChange={(e) => handleChange('financing_monthly_label', e.target.value)}
                      className="input-premium w-full"
                      placeholder="Monatliche Rate"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#999999]">🇬🇧 English</span>
                    </div>
                    <input
                      type="text"
                      value={settings.financing_monthly_label_en || 'Monthly Payment'}
                      onChange={(e) => handleChange('financing_monthly_label_en', e.target.value)}
                      className="input-premium w-full"
                      placeholder="Monthly Payment"
                    />
                  </div>
                </div>
              </div>

              {/* Interest Label */}
              <div>
                <label className="overline block mb-2">Zinssatz-Label</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#999999]">🇩🇪 Deutsch</span>
                    </div>
                    <input
                      type="text"
                      value={settings.financing_interest_label || 'effektiver Jahreszins'}
                      onChange={(e) => handleChange('financing_interest_label', e.target.value)}
                      className="input-premium w-full"
                      placeholder="effektiver Jahreszins"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#999999]">🇬🇧 English</span>
                    </div>
                    <input
                      type="text"
                      value={settings.financing_interest_label_en || 'effective annual interest'}
                      onChange={(e) => handleChange('financing_interest_label_en', e.target.value)}
                      className="input-premium w-full"
                      placeholder="effective annual interest"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-white p-4 md:p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Vorschau</h3>
            <div className="p-4 bg-[#F9F9F7] rounded-lg">
              <p className="text-sm text-[#666666] mb-2">
                Zinssatz: <strong>{settings.financing_interest_rate ?? 4.9}% p.a.</strong>
              </p>
              <p className="text-sm text-[#666666] mb-2">
                Verfügbare Laufzeiten: <strong>{(settings.financing_months || [6, 12, 24, 36, 48, 60]).join(', ')} Monate</strong>
              </p>
              <p className="text-sm text-[#666666]">
                {settings.financing_partner_text || 'In Kooperation mit unserer Partnerbank Santander'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
